import {
  Editor,
  Node,
  Range,
  Transforms,
  type Descendant,
  type Element,
} from 'slate';
import AhaEditor from '../../AhaEditor';
import type TableRange from './TableRange';
import type TableStructure from './TableStructure';
import { getTableStructureFromSelection } from './TableStructure';
import TableUtils from './TableUtils';
import { ReactEditor } from 'slate-react';
import { encodeFragment } from '../../util/fragment';
import {
  type TableCellElement,
  type TableElement,
  type TableRowElement,
} from '../../types/aha-editor';
import { HtmlSerializer } from '../../serialization';

// Serializes the fragment into HTML. By using the serialized HTML,
// this will paste better into tools like Google Sheets.
export function generateHTMLFromFragment(fragment: Descendant[]): string {
  const tempEditor = HtmlSerializer.createGenericEditor();
  tempEditor.children = fragment;
  const serializer = new HtmlSerializer(tempEditor);
  return serializer.serialize();
}

// Generates a TSV string from the table fragment, useful for pasting
// into native apps or getting nice-looking tables in plain-text
// notes.
export function generateTSVFromSelection(
  tableStructure: TableStructure,
  tableRange: TableRange
) {
  // Generate TSV for pasting into spreadsheets
  const seenCells = new Set<TableCellElement>();
  const tsvCells: string[][] = [];

  tableStructure.forEachCellInExpandedRange(
    tableRange,
    ({ tableCell: cell }, rowIndex, columnIndex) => {
      tsvCells[rowIndex] ||= [];

      if (seenCells.has(cell)) {
        tsvCells[rowIndex][columnIndex] = '';
      } else {
        seenCells.add(cell);
        const cellText = Node.string(cell);
        tsvCells[rowIndex][columnIndex] = cellText.match(/[\n\t]/)
          ? `"${cellText.replace(/"/g, '""')}"`
          : cellText;
      }
    }
  );
  return tsvCells.map((row) => row.join('\t')).join('\n');
}

const cutOrCopyTableFragment = (
  event: ClipboardEvent,
  ahaEditor: AhaEditor,
  isCut = false
) => {
  const { editor, controller } = ahaEditor;

  if (!event.clipboardData) return false;
  if (!editor.selection) return false;

  const { tableSelection } = controller.state.pluginState;

  // If the selection contains whole tables at the start / end, make sure
  // that the selection covers the entire table so all of it is copied or cut.
  if (tableSelection?.hasWholeTableSelection) {
    const backward = Range.isBackward(editor.selection);
    const edges = Range.edges(editor.selection);

    const startPath = edges[0].path;
    const endPath = edges[1].path;

    const startTable = TableUtils.parentTable(editor, startPath, 'highest');
    const endTable = TableUtils.parentTable(editor, endPath, 'highest');

    if (startTable) {
      // If there is a table at the start of the selection, make sure the selection
      // extends to the start of the table
      Transforms.setSelection(editor, {
        [backward ? 'focus' : 'anchor']: Editor.start(editor, startTable[1]),
      });
    }

    if (endTable) {
      // If there is a table at the end of the selection, make sure the selection
      // extends to the end of the table
      Transforms.setSelection(editor, {
        [backward ? 'anchor' : 'focus']: Editor.end(editor, endTable[1]),
      });
    }

    // This is just a normal selection, so let Slate handle it from this point.
    return false;
  }

  // From this point we only want to continue if the selection is within a table
  // and has an active table selection with multiple cells selected.
  const table = TableUtils.currentTable(editor);
  if (!table) return false;

  if (!tableSelection?.multipleCellsSelected || !tableSelection.tableRange) {
    return false;
  }

  const tableStructure = getTableStructureFromSelection(editor);
  if (!tableStructure) return false;

  const startCoords = tableSelection.tableRange?.startPosition;
  const endCoords = tableSelection.tableRange?.endPosition;

  if (!startCoords || !endCoords) return false;

  const startCell = tableStructure.cell(
    startCoords.columnIndex,
    startCoords.rowIndex
  );
  const endCell = tableStructure.cell(
    endCoords.columnIndex,
    endCoords.rowIndex
  );
  if (!startCell || !endCell) return false;

  const cellPathsInRange = tableStructure.cellPathsInRange(
    tableSelection.tableRange
  );

  // Iterate over each row in the table, adding a row to the fragment.
  // Push cells to the row that fall within the selected range.
  // We loop all table rows as it's important to include any empty rows that
  // exist as part of a rowspan that covers the width of the table.
  // We'll remove any rows outside the range later.
  const rows: Element[] = [];
  for (let r = 0; r < table[0].children.length; r++) {
    const row = table[0].children[r] as TableRowElement;

    rows.push({ type: 'table-row', children: [] });

    for (let c = 0; c < row.children.length; c++) {
      if (cellPathsInRange.has(`${r},${c}`)) {
        rows[r].children.push(row.children[c]);
      }
    }

    // Empty rows may exist because they didn't contain any selected
    // cells. In this case they'll be removed later.
    // They may, however, be empty because they're part of a rowspan
    // that covers the width of the table. In this case, we need to add an
    // empty text node to the row.
    if (rows[r].children.length === 0) {
      rows[r].children.push({ text: '' });
    }
  }

  // Set the clipboard data using Slate's original method, this will include
  // HTML and plain text versions.
  ReactEditor.setFragmentData(editor, event.clipboardData, 'copy');

  const tableProps: Partial<TableElement> = {};

  // Include header column if header column is set on copied table AND
  // the header column is actually in the copied range
  if (table[0].table_headerColumn && startCoords.columnIndex === 0) {
    tableProps.table_headerColumn = true;
  }
  // Include header row if header row is set on copied table AND
  // the header row is actually in the copied range
  if (table[0].table_headerRow && startCoords.rowIndex === 0) {
    tableProps.table_headerRow = true;
  }

  if (table[0].table_columnWidths) {
    // If the table has column widths, calculate the widths of the copied
    // columns and set them on the new table.
    const columnWidths = table[0].table_columnWidths.slice(
      startCoords.columnIndex,
      endCoords.columnIndex + 1
    );
    const newColumnsWidth = columnWidths.reduce((acc, width) => acc + width, 0);

    tableProps.table_columnWidths = columnWidths.map(
      (width) => (width / newColumnsWidth) * 100
    );

    // If the table has a table width set, calculate the new table width
    // based on the copied columns
    if (typeof table[0].table_width === 'number') {
      tableProps.table_width = (table[0].table_width / 100) * newColumnsWidth;
    }
  } else if (typeof table[0].table_width === 'number') {
    // If the table does not have set column widths but does have a table width,
    // calculate the width of the copied columns and set this as the new table width
    tableProps.table_width =
      (table[0].table_width / tableStructure.width) *
      (endCoords.columnIndex + 1 - startCoords.columnIndex);
  }

  const fragment: Descendant[] = [
    {
      type: 'table',
      ...tableProps,
      // Here we slice the rows array to only include the rows within the
      // selected range.
      children: rows.slice(startCoords.rowIndex, endCoords.rowIndex + 1),
    },
  ];

  // Overwrite the clipboard fragment data with our customised
  // fragment from the table selection
  event.clipboardData.setData(
    `application/${AhaEditor.clipboardFormatKey}`,
    encodeFragment(fragment)
  );

  event.clipboardData.setData('text/html', generateHTMLFromFragment(fragment));
  event.clipboardData.setData(
    'text/plain',
    generateTSVFromSelection(tableStructure, tableSelection.tableRange)
  );

  if (isCut) {
    TableUtils.clearSelectedCellContents(
      editor,
      tableSelection,
      tableStructure
    );
  }

  // Stop any other event handlers from running
  event.preventDefault();
  return true;
};

// When copying selected cells within a table, make sure the editor selection
// includes the entire contents of the start and end cells.

// After setting the selection, return false so that Slate's default copy
// handler copies the new selection to the clipboard.
const TableCutCopyPlugin = {
  name: 'Table cut/copy handler',
  handlers: {
    onCopy: (event: ClipboardEvent, ahaEditor: AhaEditor) => {
      return cutOrCopyTableFragment(event, ahaEditor);
    },
    onCut: (event: ClipboardEvent, ahaEditor: AhaEditor) => {
      return cutOrCopyTableFragment(event, ahaEditor, true);
    },
  },
};

export default TableCutCopyPlugin;
